import React, { MouseEventHandler, useCallback, useMemo } from "react";
import { auth, FirebaseError } from "firebase/app";
import { toastr } from "react-redux-toastr";
import { Box, Button, ButtonProps, Flex, Text } from "rebass";
import { FaFacebookF } from "react-icons/fa";
import capitalize from "lodash/capitalize";

import { GoogleSVG } from "../svg/GoogleSVG";

export enum ProviderType {
  google = "google",
  facebook = "facebook",
}

const provider = {
  [ProviderType.google]: new auth.GoogleAuthProvider(),
  [ProviderType.facebook]: new auth.FacebookAuthProvider(),
};

const maxWidth = 230;

const buttonProps: Record<ProviderType, Pick<ButtonProps, "color" | "bg" | "css">> = {
  [ProviderType.google]: {
    color: "zodiac",
    bg: "white",
    css: `box-shadow: 0 3px 11px 0 rgba(0, 0, 0, 0.17); max-width: ${maxWidth}px;`,
  },
  [ProviderType.facebook]: {
    color: "white",
    bg: "zodiac",
    css: `max-width: ${maxWidth}px;`,
  },
};

const icon = {
  [ProviderType.google]: GoogleSVG,
  [ProviderType.facebook]: FaFacebookF,
};

// https://firebase.google.com/docs/reference/js/firebase.auth.Auth#sign-inwith-popup
// this list of errors user does not see.
const errorCodes = [
  "auth/cancelled-popup-request",
  "auth/popup-closed-by-user",
];

interface IProps {
  type: "in" | "up",
  providerType: ProviderType,
}

export const ButtonOpenProvider: React.FC<IProps> = ({ type, providerType }) => {
  const title = useMemo(() => `Sign ${type} with ${capitalize(providerType)}`, [type, providerType]);
  const openPopUp = useCallback<MouseEventHandler>( (event) => {
    event.preventDefault();

    auth().signInWithPopup(provider[providerType])
      .catch(({ code, message }: FirebaseError) => {
        if (!errorCodes.includes(code)) {
          toastr.error(title, message);
        }
      });
  }, [title, providerType]);

  const Icon = icon[providerType];

  return (
    <Button
      type="button"
      flex={1}
      py="10px"
      {...buttonProps[providerType]}
      onClick={openPopUp}
    >
      <Flex alignItems="center">
        <Box m="5px">
          <Icon display="block" size={20} />
        </Box>

        <Text
          ml="15px"
          fontSize="14px"
          fontWeight="bold"
        >
          {title}
        </Text>
      </Flex>
    </Button>
  );
};
